import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';

import type {
  UiCreateCategory,
  ApiCategory,
  UiCategory,
} from '../../schemas/categories.schema';
import type { UiExerciseCategories } from '../../schemas/exercises.schema';
import type { ApiTranslation } from '../../schemas/translations.schema';

import { mapStatusFromApi, mapStatusToApi } from './status.mappers';
import { mapTranslationsFromApi } from './translations.mappers';
import { getLabelFromTranslations } from '../../utils';

export function mapCategoryFromApi(apiCategory: ApiCategory): UiCategory {
  const { id, createdAt, hasConfigurables, key, status, updatedAt } =
    apiCategory;
  return {
    id,
    appliesTo: apiCategory.appliesTo.map((item) => ({ value: item })),
    createdAt,
    hasConfigurables,
    key,
    // Global rows come back as `null`; the UI schema only accepts a string.
    organizationId: apiCategory?.organizationId ?? undefined,
    status: [{ value: mapStatusFromApi(status ?? 'ACTIVE') }],
    translations: apiCategory.translations.map((translation) => ({
      id: translation.id,
      locale: [
        {
          label: getLocalizedNameByLocale(translation.locale),
          value: translation.locale,
        },
      ],
      name: translation.name,
    })),
    updatedAt,
  };
}

export function mapCategoryToApi(UiCategory: UiCreateCategory) {
  const { appliesTo, status } = UiCategory;
  return {
    appliesTo: mapCategoryAppliesToToApi(appliesTo),
    status: mapStatusToApi(status[0].value),
    translations: UiCategory.translations.map((translation) => ({
      locale: translation.locale[0].value,
      name: translation.name,
    })),
  };
}

export function mapCategoryWithTagsFromApi({
  categories: apiCategories,
  locale,
}: {
  categories?: Array<{
    tags: Array<{ translations: Array<ApiTranslation>; id: string }>;
    id: string;
  }>;
  locale: Locale;
}) {
  const categories: UiExerciseCategories = {};

  if (apiCategories && apiCategories.length) {
    apiCategories.forEach((category) => {
      // TODO Add tags
      categories[category.id] = category.tags.map((tag) => {
        return {
          label: getLabelFromTranslations(
            locale,
            mapTranslationsFromApi(tag.translations),
          ),
          value: tag.id,
        };
      });
    });
  }
  return categories;
}

export function mapCategoryAppliesToToApi(appliesTo: Array<{ value: string }>) {
  return appliesTo.map((item) => item.value);
}
